import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { VideoTestimonialCard } from './eventCards';
import WrittenReviewCard from './WrittenReviewCard';
import './TestimonialsSection.css';
import videoImage from '../assets/images/video-md.png';

const TestimonialsSection = ({
  videos,
  reviews,
  onReadAllReviews,
  onWriteReview,
  eventId,
  userId,
  onReviewSubmitted,
  className = '',
  ...rest
}) => {
  const [currentVideoSlide, setCurrentVideoSlide] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [isDesktop, setIsDesktop] = useState(true);
  
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 768);
      setIsDesktop(width > 900);
    };
    
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  const videosPerPage = isDesktop ? 4 : 3;
  const totalSlides = Math.ceil(videos.length / videosPerPage);

  // Show all videos on mobile/tablet, paginate on desktop
  const displayedVideos = isMobile ? videos : videos.slice(
    currentVideoSlide * videosPerPage,
    (currentVideoSlide + 1) * videosPerPage
  );

  const featuredReview = reviews && reviews.length > 0 ? reviews[0] : null;

  return (
    <section className={`testimonials-section ${className}`.trim()} {...rest}>
      <div className="testimonials-section__grid">
        {/* Video Testimonials */}
        <div className="testimonials-section__videos">
          <div className="testimonials-section__heading">
            {/* <i className="fas fa-video testimonials-section__icon" aria-hidden="true"></i> */}
            <img className="testimonials-section__icon video-icon" src={videoImage}/>
            <h2>Video Testimonials</h2>
          </div>
          {videos.length === 0 ? (
            <p className="testimonials-section__empty">Videos will be available soon.</p>
          ) : (
            <div className="testimonials-section__video-carousel">
              <div className="testimonials-section__video-grid">
                {displayedVideos.map((video, index) => (
                  <VideoTestimonialCard
                    key={video.video_id || video.id || index}
                    thumbnail={video.thumbnail || video.image_url}
                    title={video.title || video.name}
                    location={video.location || video.city}
                    likes={video.likes || video.total_likes}
                    dislikes={video.dislikes || video.total_dislikes}
                    onPlay={video.onPlay}
                  />
                ))}
              </div>
              {!isMobile && totalSlides > 1 && (
                <div className="testimonials-section__pagination">
                  {Array.from({ length: totalSlides }).map((_, index) => (
                    <button
                      key={index}
                      type="button"
                      className={`testimonials-section__dot ${
                        currentVideoSlide === index ? 'testimonials-section__dot--active' : ''
                      }`}
                      onClick={() => setCurrentVideoSlide(index)}
                      aria-label={`Go to slide ${index + 1}`}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Written Reviews */}
        <div id="written-reviews-section" className="testimonials-section__reviews">
          <div className="testimonials-section__heading written-review">
            <h2>Written Reviews</h2>
          </div>
          <WrittenReviewCard
            review={featuredReview}
            reviews={reviews}
            onReadAllReviews={onReadAllReviews}
            onWriteReview={onWriteReview}
            eventId={eventId}
            userId={userId}
            onReviewSubmitted={onReviewSubmitted}
          />
        </div>
      </div>
    </section>
  );
};

TestimonialsSection.propTypes = {
  videos: PropTypes.arrayOf(
    PropTypes.shape({
      video_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      thumbnail: PropTypes.string,
      title: PropTypes.string,
      location: PropTypes.string,
      likes: PropTypes.number,
      dislikes: PropTypes.number,
      onPlay: PropTypes.func,
    })
  ),
  reviews: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      author: PropTypes.string,
      location: PropTypes.string,
    })
  ),
  onReadAllReviews: PropTypes.func,
  onWriteReview: PropTypes.func,
  eventId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onReviewSubmitted: PropTypes.func,
  className: PropTypes.string,
};

TestimonialsSection.defaultProps = {
  videos: [],
  reviews: [],
  onReadAllReviews: () => {},
  onWriteReview: () => {},
  eventId: null,
  userId: null,
  onReviewSubmitted: () => {},
  className: '',
};

export default TestimonialsSection;

